/* RESPONSIBLE TEAM: team-reporting */

import Model, { attr } from '@ember-data/model';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';
import { cached } from 'tracked-toolbox';
import { without } from 'underscore';

export enum SchedulePreset {
  ONE_MINUTE = 'one_minute',
  DAILY = 'daily',
  CUSTOM = 'custom',
  EVERY_MONDAY = 'every_monday',
  EVERY_FRIDAY = 'every_friday',
  FIRST_DAY_OF_MONTH = 'first_day_of_month',
  LAST_DAY_OF_MONTH = 'last_day_of_month',
}

export enum Step {
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

export enum DayOfWeek {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

export const EVERY_DAY = [
  DayOfWeek.SUNDAY,
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
];

export enum DayPosition {
  FIRST = 'first',
  LAST = 'last',
  SECOND = 'second',
  THIRD = 'third',
}

export interface DeliveryTime {
  hour: number;
  minute: number;
  timezone: string;
}

export interface Interval {
  step: string;
  value: number;
  dayPosition?: DayPosition;
}
export interface ExportRule {
  daysOfWeek: Array<number>;
  deliveryTime: DeliveryTime;
  interval: Interval;
}

export interface DatasetExportConfig {
  datasetId: string;
  sortByAttributeId: string;
  attributeIds: Array<string>;
  filter: LogicalFilter;
  dateRangeSelection: string;
}

function hashAttr(options?: { excludeKeys?: string[] }) {
  return attr('hash', options as any);
}

export default class DatasetExportSchedule extends Model {
  @service declare intl: IntlService;
  @service declare datasetExportScheduleService: DatasetExportScheduleService;

  @attr('string') declare name: string;
  @hashAttr() declare exportRule: ExportRule;
  @attr('date') declare readonly deliveryAt: Date | undefined;
  @hashAttr({ excludeKeys: ['filter'] }) declare exportConfig: DatasetExportConfig;

  updateExportRule(exportRule: ExportRule) {
    this.set('exportRule', exportRule);
  }

  get timezone() {
    return this.exportRule.deliveryTime.timezone;
  }

  formatDeliveryAt(deliveryAt?: Date) {
    if (deliveryAt) {
      return this.intl.formatDate(deliveryAt, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } else {
      return undefined;
    }
  }

  get formattedDeliveryAt() {
    return this.formatDeliveryAt(this.deliveryAt);
  }

  @cached
  get isPreset() {
    // look at every preset except custom and see if the rule matches
    let presets: SchedulePreset[] = Object.values(SchedulePreset);
    return without(presets, SchedulePreset.CUSTOM).some((preset) =>
      this.datasetExportScheduleService.isPresetRule(this.exportRule, preset, this.timezone),
    );
  }
}
